import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { logEvent } from "~/utils/firebase";
import { useI18n } from "~/composables/useI18n";
import { useUserStore } from "~/stores/user";
import { stripLocalePrefix } from "~/utils/i18n-locale-routes";

/*
埋点文档：
https://alidocs.dingtalk.com/i/nodes/ndMj49yWjX55xn0miR7Aoeg9J3pmz5aA?corpId=ding5d30125d52da83d1bc961a6cb783455b&utm_medium=im_card&iframeQuery=utm_medium%3Dim_card%26utm_source%3Dim&utm_scene=team_space&utm_source=im
*/

const PAGE_NAME_MAP = {
	"/": "home",
	"/seer/master-fortune": "master_fortune",
	"/seer/service-institution": "service_institution",
	"/seer/aboutus": "about_us",
};

export function cleanParams(params) {
	return Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		)
	);
}

export function mapTrackingLanguage(locale) {
	return locale === "en" ? "en" : "zh_hant";
}

export function resolvePageName(path) {
	const normalizedPath = stripLocalePrefix(path);
	if (PAGE_NAME_MAP[normalizedPath]) return PAGE_NAME_MAP[normalizedPath];
	if (!normalizedPath || normalizedPath === "/") return "home";

	return normalizedPath.replace(/^\//, "").replace(/\//g, "_");
}

/**
 * 全站公共组件埋点（顶部导航、页脚等）的基础 tracking composable。
 */
export function useSiteComponentTracking(deviceType = "pc") {
	const route = useRoute();
	const { locale } = useI18n();
	const { isLogin } = storeToRefs(useUserStore());

	const baseParams = computed(() => {
		const params = {
			page_name: resolvePageName(route.path),
			page_language: mapTrackingLanguage(locale?.value || "zh-TW"),
			device_type: deviceType,
			login_status: isLogin.value ? "logged_in" : "guest",
		};

		const utmSource = route.query.utm_source;
		if (utmSource) params.utm_source = String(utmSource);

		return cleanParams(params);
	});

	function track(eventName, params = {}) {
		logEvent(eventName, cleanParams({ ...baseParams.value, ...params }));
	}

	function trackHomepageView() {
		if (resolvePageName(route.path) !== "home") return;
		track("homepage_view");
	}

	return {
		track,
		trackHomepageView,
		baseParams,
	};
}

/** @deprecated 使用 useSiteComponentTracking */
export const useHeaderTracking = useSiteComponentTracking;

export const GLOBAL_SEARCH_CLOSE_TYPES = {
	MASK: "mask",
	ESC: "esc",
	NAVIGATE: "navigate",
	UNKNOWN: "unknown",
};

export const GLOBAL_SEARCH_RESULT_TYPES = {
	MENTOR: "mentor",
	SERVICE: "service",
	CONTENT: "content",
	EXPERT: "expert",
};

const SEARCH_TAB_RESULT_TYPE_MAP = {
	0: GLOBAL_SEARCH_RESULT_TYPES.MENTOR,
	1: GLOBAL_SEARCH_RESULT_TYPES.SERVICE,
	2: GLOBAL_SEARCH_RESULT_TYPES.CONTENT,
	3: GLOBAL_SEARCH_RESULT_TYPES.EXPERT,
};

export function mapSearchTabToResultType(tabType) {
	return SEARCH_TAB_RESULT_TYPE_MAP[String(tabType)] || "unknown";
}

export function getGlobalSearchKeywordLength(keyword) {
	return String(keyword || "").trim().length;
}

export function resolveGlobalSearchKeywordType(keyword, hotTags = []) {
	const value = String(keyword || "").trim();
	if (!value) return undefined;
	if (Array.isArray(hotTags) && hotTags.includes(value)) return "topic";
	return "unknown";
}

export function resolveGlobalSearchPlaceholderType(keyword) {
	return String(keyword || "").trim() ? undefined : "default";
}

export function getGlobalSearchMentorType(areaType) {
	return Number(areaType) === 10 ? "famous" : "normal";
}

/**
 * 全站搜索曝光：元素进入视窗 50% 且停留 1 秒后触发，同一 impressionKey 去重。
 * 组件内请直接调用 track('global_search_hot_mentor_impression', params) 等，事件名写在调用处。
 */
export function createGlobalSearchImpressionTracker(track) {
	const impressedKeys = new Set();
	const dwellTimers = new Map();
	let observer = null;

	const clearDwellTimer = key => {
		if (!dwellTimers.has(key)) return;
		window.clearTimeout(dwellTimers.get(key));
		dwellTimers.delete(key);
	};

	const ensureObserver = () => {
		if (observer || !import.meta.client) return;
		observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					const key = entry.target.dataset.globalSearchImpressionKey;
					if (!key || impressedKeys.has(key)) return;

					if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
						if (dwellTimers.has(key)) return;
						const paramsJson = entry.target.dataset.globalSearchImpressionParams;
						const timer = window.setTimeout(() => {
							if (impressedKeys.has(key)) return;
							impressedKeys.add(key);
							let params = {};
							if (paramsJson) {
								try {
									params = JSON.parse(paramsJson);
								} catch {
									params = {};
								}
							}
							track(params.event_name, params.payload || {});
						}, 1000);
						dwellTimers.set(key, timer);
						return;
					}

					clearDwellTimer(key);
				});
			},
			{ threshold: [0.5] }
		);
	};

	const bind = (el, impressionKey, eventName, payload = {}) => {
		if (!el || !impressionKey || !eventName) return;
		ensureObserver();
		if (impressedKeys.has(impressionKey)) return;

		el.dataset.globalSearchImpressionKey = impressionKey;
		el.dataset.globalSearchImpressionParams = JSON.stringify({
			event_name: eventName,
			payload,
		});
		observer.observe(el);
	};

	const unbind = el => {
		if (!el || !observer) return;
		const key = el.dataset.globalSearchImpressionKey;
		clearDwellTimer(key);
		observer.unobserve(el);
		delete el.dataset.globalSearchImpressionKey;
		delete el.dataset.globalSearchImpressionParams;
	};

	const reset = () => {
		impressedKeys.clear();
		dwellTimers.forEach(timer => window.clearTimeout(timer));
		dwellTimers.clear();
		observer?.disconnect();
		observer = null;
	};

	return { bind, unbind, reset };
}

/**
 * 全站搜索埋点 composable。
 * 请使用 track('global_search_modal_view', params) —— 事件名直接写在调用处，便于搜索与对照文档。
 */
export function useGlobalSearchTracking(deviceType = "pc", options = {}) {
	const { track, baseParams } = useSiteComponentTracking(deviceType);
	const inputFocusFired = ref(false);

	const resolveTriggerPage = () => {
		const page = options.triggerPage;
		if (page && typeof page === "object" && "value" in page) {
			const value = page.value;
			if (value) return value;
		} else if (page) {
			return page;
		}
		return baseParams.value.page_name;
	};

	const resolveTriggerPosition = () => {
		const position = options.triggerPosition;
		if (position && typeof position === "object" && "value" in position) {
			return position.value || "top_search";
		}
		return position || "top_search";
	};

	const withSearchContext = (params = {}) =>
		cleanParams({
			trigger_page: resolveTriggerPage(),
			...params,
		});

	const trackGlobalSearch = (eventName, params = {}) => {
		track(eventName, withSearchContext(params));
	};

	const resetSession = () => {
		inputFocusFired.value = false;
	};

	const trackInputFocusOnce = params => {
		if (inputFocusFired.value) return;
		inputFocusFired.value = true;
		trackGlobalSearch("global_search_input_focus", params);
	};

	return {
		track: trackGlobalSearch,
		baseParams,
		resetSession,
		trackInputFocusOnce,
		resolveTriggerPage,
		resolveTriggerPosition,
	};
}

export function getEmailDomain(email) {
	if (!email || !email.includes("@")) return undefined;
	const domain = email.split("@")[1]?.trim().toLowerCase();
	return domain || undefined;
}

const SOCIAL_PLATFORM_MAP = {
	"YouTube.png": "youtube",
	"WhatsApp.png": "whatsapp",
	"x.png": "x",
	"facebook.png": "facebook",
	"Line.png": "line",
	"ins.png": "instagram",
	"tiktok.png": "tiktok",
	"facebookIcon.png": "facebook",
	"insIcon.png": "instagram",
	"lineIcon.png": "line",
	"threadsIcon.png": "threads",
	"tiktokIcon.png": "tiktok",
	"whatsappIcon.png": "whatsapp",
	"youtubeIcon.png": "youtube",
};

export function resolveSocialPlatform(icon) {
	return SOCIAL_PLATFORM_MAP[icon] || icon?.replace(/\.png$/i, "").toLowerCase();
}

export const LOGIN_AUTH_METHODS = {
	PHONE: "phone",
	EMAIL: "email",
};

export const LOGIN_TRIGGER_SOURCES = {
	HEADER_ACCOUNT: "header_account",
	URL_PARAM: "url_param",
	THIRD_PARTY_BIND: "third_party_bind",
	PAGE_ACTION: "page_action",
	UNKNOWN: "unknown",
};

const SOCIAL_LOGIN_PROVIDER_MAP = {
	google: "google",
	google_third: "google",
	facebook: "facebook",
	line: "line",
	apple: "apple",
	phone: "phone",
	email: "email",
	mobile: "phone",
};

/** 登录埋点平台：pc / android / ios */
export function resolveSocialLoginPlatform() {
	if (!import.meta.client) return "pc";
	const ua = navigator.userAgent || "";
	if (/Android/i.test(ua)) return "android";
	if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
	return "pc";
}

export function formatLoginCountryCode(code) {
	if (!code) return undefined;
	const raw = String(code).trim();
	if (!raw) return undefined;
	if (raw.startsWith("+")) return raw;
	if (raw.startsWith("00")) return `+${raw.slice(2)}`;
	return `+${raw}`;
}

export function normalizeLoginResult(success) {
	return success ? "success" : "fail";
}

export function normalizeProvider(provider) {
	return SOCIAL_LOGIN_PROVIDER_MAP[provider] || provider;
}

/**
 * 统一登录方式点击埋点：social_login_click
 * 仅 provider + platform
 */
export function trackSocialLoginClick(provider) {
	logEvent(LOGIN_EVENTS.SOCIAL_LOGIN_CLICK, {
		provider: normalizeProvider(provider),
		platform: resolveSocialLoginPlatform(),
	});
}

/**
 * 统一登录成功埋点：social_login_success
 * 仅 provider + platform（含第三方补绑邮箱后成功）
 */
export function trackSocialLoginSuccess(provider) {
	logEvent(LOGIN_EVENTS.SOCIAL_LOGIN_SUCCESS, {
		provider: normalizeProvider(provider),
		platform: resolveSocialLoginPlatform(),
	});
}

export const LOGIN_EVENTS = {
	MODAL_VIEW: "login_modal_view",
	CLOSE_CLICK: "login_close_click",
	TAB_CLICK: "login_tab_click",
	COUNTRY_CODE_CLICK: "login_country_code_click",
	PHONE_INPUT_FOCUS: "login_phone_input_focus",
	EMAIL_INPUT_FOCUS: "login_email_input_focus",
	VERIFICATION_CODE_SEND_CLICK: "verification_code_send_click",
	VERIFICATION_CODE_SEND_RESULT: "verification_code_send_result",
	VERIFICATION_CODE_INPUT_FOCUS: "verification_code_input_focus",
	KEEP_LOGIN_TOGGLE: "keep_login_toggle",
	SUBMIT_CLICK: "login_submit_click",
	SUBMIT_RESULT: "login_submit_result",
	SOCIAL_LOGIN_CLICK: "social_login_click",
	SOCIAL_LOGIN_SUCCESS: "social_login_success",
	HELP_CLICK: "login_help_click",
};

const LOGIN_MODAL_PARAM_EVENTS = new Set([
	LOGIN_EVENTS.MODAL_VIEW,
	LOGIN_EVENTS.CLOSE_CLICK,
]);

const LOGIN_ONCE_EVENTS = new Set([
	LOGIN_EVENTS.MODAL_VIEW,
	LOGIN_EVENTS.PHONE_INPUT_FOCUS,
	LOGIN_EVENTS.EMAIL_INPUT_FOCUS,
]);

/**
 * 登录弹窗埋点 composable。
 * 请使用 logEvent('login_modal_view', params) —— 事件名直接写在调用处，便于搜索与对照文档。
 */
export function useLoginModalTracking(deviceType = "pc", options = {}) {
	const { track, baseParams } = useSiteComponentTracking(deviceType);
	const firedEvents = new Set();

	const resolveTriggerSource = () => {
		const source = options.triggerSource;
		if (source && typeof source === "object" && "value" in source) {
			return source.value || LOGIN_TRIGGER_SOURCES.UNKNOWN;
		}
		return source || LOGIN_TRIGGER_SOURCES.UNKNOWN;
	};

	const withModalParams = (params = {}) =>
		cleanParams({
			trigger_source: resolveTriggerSource(),
			page_name: baseParams.value.page_name,
			login_status: baseParams.value.login_status,
			...params,
		});

	const resetSession = () => {
		firedEvents.clear();
	};

	/**
	 * Firebase 登录弹窗埋点（底层即 logEvent）。
	 * @param {string} eventName 文档中的 Firebase event，如 login_modal_view
	 * @param {Record<string, unknown>} [params]
	 */
	function logEvent(eventName, params = {}) {
		const normalized = { ...params };

		if (normalized.country_code) {
			normalized.country_code = formatLoginCountryCode(normalized.country_code);
		}

		if (normalized.provider) {
			normalized.provider = normalizeProvider(normalized.provider);
			if (!normalized.platform) {
				normalized.platform = resolveSocialLoginPlatform();
			}
		}

		if ("success" in normalized) {
			normalized.result = normalizeLoginResult(normalized.success);
			if (normalized.success) {
				delete normalized.error_code;
			}
			delete normalized.success;
		}

		const payload = LOGIN_MODAL_PARAM_EVENTS.has(eventName)
			? withModalParams(normalized)
			: cleanParams(normalized);

		if (eventName === LOGIN_EVENTS.VERIFICATION_CODE_INPUT_FOCUS) {
			const onceKey = `${eventName}_${normalized.auth_method || "unknown"}`;
			if (firedEvents.has(onceKey)) return;
			firedEvents.add(onceKey);
			track(eventName, payload);
			return;
		}

		if (LOGIN_ONCE_EVENTS.has(eventName)) {
			if (firedEvents.has(eventName)) return;
			firedEvents.add(eventName);
		}

		track(eventName, payload);
	}

	return {
		logEvent,
		resetSession,
		LOGIN_EVENTS,
	};
}
